/* eslint-disable complexity */
import React, { useContext } from 'react';
import classNames from 'classnames';
import Typography from 'carbon-react/lib/components/typography';

import { ContactDetails } from '@futrli-sdk/predict';
import { FormattedMessage, FormattedNumber } from '@futrli/localisation';

import { CUSTOMER_SUPPLIER_KIND } from '../const';
import { penceToAmount } from '../../../../../core/formatters';
import { PredictManagementContext } from '../../../../../predict-management/context/predict-management-context';

import * as styles from './overview.scss';

export const Overview = ({ activeData }: { activeData?: ContactDetails }) => {
  const { currency } = useContext(PredictManagementContext);

  if (!activeData) {
    return null;
  }

  if (
    activeData.amount_due === 0 &&
    activeData.unpaid_invoices === 0 &&
    activeData.amount_due_7_days === 0 &&
    activeData.late_invoices === 0 &&
    activeData.amount_late === 0
  ) {
    return null;
  }

  return (
    <div>
      <Typography mb="8px" fontSize="18px" fontWeight="500">
        <FormattedMessage id="predict.overview" defaultMessage="Overview" />
      </Typography>

      {activeData.amount_due > 0 && activeData.unpaid_invoices > 0 ? (
        <div className={styles.overviewRow}>
          <div className={classNames(styles.dot, styles.dotSpace)} />
          <div className={styles.basicFont}>
            {activeData.type === CUSTOMER_SUPPLIER_KIND.CUSTOMER ? (
              <FormattedMessage
                id="predict.you_are_owed_value_over_value"
                defaultMessage="You are owed {amount_due} over {unpaid_invoices, number} {unpaid_invoices, plural, one {invoice} other {invoices}}"
                values={{
                  amount_due: (
                    <FormattedNumber
                      style="currency"
                      currency={currency!}
                      value={penceToAmount(activeData.amount_due)}
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                    />
                  ),
                  unpaid_invoices: activeData.unpaid_invoices
                }}
              />
            ) : (
              <FormattedMessage
                id="predict.you_owe_value_over_value"
                defaultMessage="You owe {amount_due} over {unpaid_invoices, number} {unpaid_invoices, plural, one {bill} other {bills}}"
                values={{
                  amount_due: (
                    <FormattedNumber
                      style="currency"
                      currency={currency!}
                      value={penceToAmount(activeData.amount_due)}
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                    />
                  ),
                  unpaid_invoices: activeData.unpaid_invoices
                }}
              />
            )}
          </div>
        </div>
      ) : null}
      {activeData.amount_due_7_days > 0 ? (
        <div className={styles.overviewRow}>
          <div className={classNames(styles.dot, styles.dotSpace)} />
          <div className={styles.basicFont}>
            <FormattedMessage
              id="predict.value_due_7_days"
              defaultMessage="{amount_due_7_days} is due within 7 days"
              values={{
                amount_due_7_days: (
                  <FormattedNumber
                    style="currency"
                    currency={currency!}
                    value={penceToAmount(activeData.amount_due_7_days)}
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                )
              }}
            />
          </div>
        </div>
      ) : null}
      {activeData.unpaid_invoices > 0 ? (
        <div className={styles.overviewRow}>
          <div className={classNames(styles.dot, styles.dotSpace)} />
          <div className={styles.basicFont}>
            {activeData.type === CUSTOMER_SUPPLIER_KIND.CUSTOMER ? (
              <FormattedMessage
                id="predict.recently_they_are_not_paying_on_time"
                defaultMessage="Recently they are not paying on time"
              />
            ) : (
              <FormattedMessage
                id="predict.recently_you_are_not_paying_on_time"
                defaultMessage="Recently you are not paying on time"
              />
            )}
          </div>
        </div>
      ) : null}
      {activeData.late_invoices && activeData.amount_late > 0 ? (
        <div className={styles.overviewRow}>
          <div className={classNames(styles.dot, styles.dotSpace)} />
          <div className={styles.basicFont}>
            {activeData.type === CUSTOMER_SUPPLIER_KIND.CUSTOMER ? (
              <FormattedMessage
                id="predict.value_late_invoices_amount_worth"
                defaultMessage="{late_invoices, number} {late_invoices, plural, one {invoice} other {invoices}} 
                worth {amount_late} {late_invoices, plural, one {is} other {are}} late"
                values={{
                  late_invoices: activeData.late_invoices,
                  amount_late: (
                    <FormattedNumber
                      style="currency"
                      currency={currency!}
                      value={penceToAmount(activeData.amount_late)}
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                    />
                  )
                }}
              />
            ) : (
              <FormattedMessage
                id="predict.value_late_bills_amount"
                defaultMessage="{late_invoices, number} {late_invoices, plural, one {bill} other {bills}} worth {amount_late} 
                {late_invoices, plural, one {is} other {are}} late"
                values={{
                  late_invoices: activeData.late_invoices,
                  amount_late: (
                    <FormattedNumber
                      style="currency"
                      currency={currency!}
                      value={penceToAmount(activeData.amount_late)}
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                    />
                  )
                }}
              />
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};
